// === ./src/frontend/components/CTASection.jsx ===
import React, { useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Chip,
  alpha,
} from "@mui/material";
import { ArrowForward, CheckCircle } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { float } from "./animations";
import { TranslateContext } from "../../context/TranslateContext";

const CTASection = ({ theme }) => {
  const history = useHistory();
  const { lang } = useContext(TranslateContext);

  return (
    <Box sx={{ py: { xs: 10, md: 14 } }}>
      <Container maxWidth="md">
        <Box
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            position: "relative",
            p: { xs: 4, md: 7 },
            textAlign: "center",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.75)} 100%)`,
            boxShadow: `0 24px 80px ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          {/* Decorative circles */}
          {[
            { size: 300, top: "-20%", right: "-10%", opacity: 0.1 },
            { size: 200, bottom: "-15%", left: "-8%", opacity: 0.08 },
            { size: 120, top: "30%", left: "20%", opacity: 0.06 },
          ].map((c, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                width: c.size,
                height: c.size,
                borderRadius: "50%",
                border: `1px solid ${alpha("#fff", c.opacity * 3)}`,
                bgcolor: alpha("#fff", c.opacity),
                top: c.top,
                bottom: c.bottom,
                left: c.left,
                right: c.right,
                animation: `${float} ${4 + i}s ease-in-out infinite`,
                animationDelay: `${i * 0.5}s`,
                pointerEvents: "none",
              }}
            />
          ))}

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Chip
              label={lang?.ctaChip || "🚀 Get Started Today"}
              size="small"
              sx={{
                mb: 3,
                fontWeight: 700,
                bgcolor: alpha("#fff", 0.15),
                color: "#fff",
                border: `1px solid ${alpha("#fff", 0.3)}`,
                backdropFilter: "blur(10px)",
              }}
            />
            <Typography
              variant="h2"
              fontWeight={800}
              color="#fff"
              sx={{
                fontSize: { xs: "2rem", md: "3rem" },
                letterSpacing: "-0.03em",
                mb: 2,
                lineHeight: 1.2,
              }}
            >
              {lang?.ctaTitle || "Start Downloading to the Cloud Today"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: alpha("#fff", 0.8),
                mb: 4,
                maxWidth: 480,
                mx: "auto",
                lineHeight: 1.7,
              }}
            >
              {lang?.ctaSubtitle ||
                "Join thousands of users who've ditched slow desktop torrent clients. Free plan available — no credit card required."}
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => history.push("/user/register")}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  fontSize: "1rem",
                  bgcolor: "#fff",
                  color: theme.palette.primary.main,
                  boxShadow: `0 8px 24px ${alpha("#000", 0.2)}`,
                  "&:hover": {
                    bgcolor: alpha("#fff", 0.92),
                    transform: "translateY(-2px)",
                    boxShadow: `0 12px 32px ${alpha("#000", 0.25)}`,
                  },
                  transition: "all 0.25s ease",
                }}
              >
                {lang?.ctaCtaPrimary || "Create Free Account"}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => history.push("/user/login")}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2.5,
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderColor: alpha("#fff", 0.5),
                  color: "#fff",
                  "&:hover": {
                    borderColor: "#fff",
                    bgcolor: alpha("#fff", 0.1),
                  },
                }}
              >
                {lang?.ctaCtaSecondary || "Sign In"}
              </Button>
            </Stack>

            {/* Trust row */}
            <Stack
              direction="row"
              spacing={3}
              justifyContent="center"
              mt={4}
              flexWrap="wrap"
              useFlexGap
            >
              {[
                lang?.ctaTrust1 || "No credit card required",
                lang?.ctaTrust2 || "Free plan forever",
                lang?.ctaTrust3 || "Cancel anytime",
              ].map((t) => (
                <Stack
                  key={t}
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                >
                  <CheckCircle
                    sx={{ fontSize: 14, color: alpha("#fff", 0.8) }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: alpha("#fff", 0.8), fontWeight: 600 }}
                  >
                    {t}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default CTASection;
